import styles from './SpendingLimitCard.module.css';
import { ValidLimits } from './components/ValidLimits';
import { ExpiredLimits } from './components/ExpiredLimits';
import { DeclinedLimits } from './components/DeclinedLimits';
import { Application } from '../Dashboard.types';
import { getSpendingLimitStatus } from '../Dashboard.helpers';
import { Modal, Button } from '@iwoca/orion';
import { useState } from 'react';
import { useGetANTFormalOffers } from '../hooks/useGetANTFormalOffers';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { useGetBuyerSpendingLimit } from '@api/lendingAPI/lapiHooks';

const SpendingLimitCard = ({
    setIsSpendingLimitDrawerOpen,
    refreshSpendingLimit,
    loadingRefreshSpendingLimit,
    application
}: {
    setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
    refreshSpendingLimit: () => Promise<void>;
    loadingRefreshSpendingLimit: boolean;
    application: Application | null;
}) => {
    const { buyerSpendingLimit, loadingBuyerSpendingLimit } =
        useGetBuyerSpendingLimit();
    const activeSpendingLimit = buyerSpendingLimit?.active_spending_limit;

    const { offers: antOffers } = useGetANTFormalOffers();
    const coreANTOffers = antOffers.filter(
        (offer) => offer?.product_type !== 'iwocapay'
    );
    const [showSupersedeModal, setShowSupersedeModal] =
        useState<boolean>(false);

    if (loadingBuyerSpendingLimit || !activeSpendingLimit) return null;

    // Legacy compatibility to remove the old endpoint first
    const status = getSpendingLimitStatus({
        spendingLimits: { isValid: activeSpendingLimit.is_valid },
        application
    });

    const handleClick = async ({
        supersedeAccepted = false
    }: { supersedeAccepted?: boolean } = {}) => {
        if (coreANTOffers.length > 0 && !supersedeAccepted) {
            return setShowSupersedeModal(true);
        }

        setIsSpendingLimitDrawerOpen(true);
        await refreshSpendingLimit();
    };

    const acceptConditions = async () => {
        setShowSupersedeModal(false);
        await handleClick({ supersedeAccepted: true });
    };

    return (
        <>
            <Modal
                isOpen={showSupersedeModal}
                onClose={() => setShowSupersedeModal(false)}
            >
                <h1 className={styles.creditFacilityWarningTitle}>
                    Once you hit continue we’ll need to re-run your existing
                    Flexi-Loan offer for{' '}
                    <FormattedCurrency
                        value={coreANTOffers[0]?.available_credit || 0}
                    />
                </h1>
                <p className={styles.creditFacilityWarningText}>
                    Don’t worry - you won’t need to give us any more documents
                    to do this. To see your Flexi-Loan offer, go to your iwoca
                    dashboard.
                </p>
                <div className={styles.creditFacilityButtonContainer}>
                    <Button onClick={acceptConditions}>
                        Continue with iwocaPay
                    </Button>
                    <Button
                        onClick={() => setShowSupersedeModal(false)}
                        variant="tertiary"
                    >
                        Go back
                    </Button>
                </div>
            </Modal>
            <div className={styles.outerContainer}>
                {status === 'valid' && (
                    <ValidLimits
                        setIsSpendingLimitDrawerOpen={
                            setIsSpendingLimitDrawerOpen
                        }
                        spendingLimit={activeSpendingLimit}
                    />
                )}
                {status === 'expired' && (
                    <ExpiredLimits
                        setIsSpendingLimitDrawerOpen={
                            setIsSpendingLimitDrawerOpen
                        }
                        spendingLimit={activeSpendingLimit}
                        loading={loadingRefreshSpendingLimit}
                        handleClick={handleClick}
                    />
                )}
                {status === 'declined' && (
                    <DeclinedLimits
                        setIsSpendingLimitDrawerOpen={
                            setIsSpendingLimitDrawerOpen
                        }
                        spendingLimit={activeSpendingLimit}
                        loading={loadingRefreshSpendingLimit}
                        handleClick={handleClick}
                        application={application!}
                    />
                )}
            </div>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default SpendingLimitCard;
